import { useMutation } from '@tanstack/react-query';

import {
  useInvalidateCompaniesList,
  useOptimisticUpdateCompanyById,
} from '@/entities/company';
import {
  ICompanyDto,
  IUpdateCompanyRequestDto,
  updateCompanyById,
} from '@/shared/api/companies';
import { useNotifications } from '@/shared/ui/Notifications';

export interface IUpdateMutationData extends IUpdateCompanyRequestDto {
  companyId: string;
}

export function useUpdateCompany() {
  const { addNotification } = useNotifications();
  const invalidateCompaniesList = useInvalidateCompaniesList();
  const optimisticUpdateCompanyById = useOptimisticUpdateCompanyById();

  const updateCompanyMutation = useMutation({
    mutationFn: ({ companyId, ...updateValues }: IUpdateMutationData) =>
      updateCompanyById(companyId, updateValues),
    onSuccess: async (data) => {
      optimisticUpdateCompanyById(data);
      await invalidateCompaniesList();
    },
    onError: (error) => {
      addNotification({
        title: error.message,
        caption: error?.data?.details,
      });
    },
  });

  const updateCompany = async (
    data: IUpdateMutationData,
    onChanged?: () => void
  ): Promise<ICompanyDto> => {
    return updateCompanyMutation.mutateAsync(data, { onSuccess: onChanged });
  };

  return { updateCompany, ...updateCompanyMutation };
}
